import type {
  TParentToChildPath,
  TChildToParentPath,
} from '@home/hooks/useEmbedChildBridge';
import { GAMEWIKI_PERSONAL_SPACE_ID } from './constants';

/** 子应用知识库列表 path（不含 /kb） */
export const GAMEWIKI_KB_LIST_CHILD_PATH = '/platform/knowledge-bases';

/** 子应用知识库详情 path 前缀（不含 /kb） */
export const GAMEWIKI_KB_DETAIL_CHILD_PREFIX = '/platform/knowledge-bases/';

const splitPathAndSearch = (
  fullPath: string,
): {
  path: string;
  search: string;
} => {
  const i = fullPath.indexOf('?');
  if (i === -1) return { path: fullPath, search: '' };
  return { path: fullPath.slice(0, i), search: fullPath.slice(i) };
};

const normalizePath = (path: string): string => {
  if (path.length > 1 && path.endsWith('/')) {
    return path.replace(/\/+$/, '');
  }
  return path;
};

const getQueryParam = (search: string, key: string): string | null => {
  if (!search) return null;
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  return params.get(key);
};

/** 合并 query：可覆盖键、删除已映射到 path 的键 */
const buildSearch = (
  search: string,
  overrides: Record<string, string>,
  removeKeys: string[] = [],
): string => {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  removeKeys.forEach(key => params.delete(key));
  Object.entries(overrides).forEach(([key, value]) => {
    params.set(key, value);
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

/**
 * 父地址栏 → 子应用 fullPath
 *
 * 要求1：/gamewiki/space/{spaceId} → /platform/knowledge-bases?tenantId={spaceId}
 * 要求2：/gamewiki/space/{spaceId}/kb/{kbId} → /platform/knowledge-bases/{kbId}?fromTenantId={spaceId}
 * 个人空间：spaceId 为 personal 时子路由不带 tenantId / fromTenantId
 */
export const gameWikiParentToChildPath: TParentToChildPath = (
  businessPathname,
  search,
  ctx,
) => {
  const mount = ctx.parentMountPath.replace(/\/$/, '');
  const pathname = normalizePath(businessPathname);

  const detailMatch = pathname.match(
    new RegExp(`^${mount}/space/([^/]+)/kb/([^/]+)$`),
  );
  if (detailMatch) {
    const spaceId = detailMatch[1];
    const kbId = detailMatch[2];
    const isPersonalSpace = spaceId === GAMEWIKI_PERSONAL_SPACE_ID;
    const childSearch = isPersonalSpace
      ? buildSearch(search, {}, ['tenantId', 'fromTenantId'])
      : buildSearch(search, { fromTenantId: spaceId }, ['tenantId']);
    return `${GAMEWIKI_KB_DETAIL_CHILD_PREFIX}${kbId}${childSearch}`;
  }

  const spaceMatch = pathname.match(
    new RegExp(`^${mount}/space/([^/]+)$`),
  );
  if (spaceMatch) {
    const spaceId = spaceMatch[1];
    const isPersonalSpace = spaceId === GAMEWIKI_PERSONAL_SPACE_ID;
    const childSearch = isPersonalSpace
      ? buildSearch(search, {}, ['tenantId', 'fromTenantId'])
      : buildSearch(search, { tenantId: spaceId }, ['fromTenantId']);
    return `${GAMEWIKI_KB_LIST_CHILD_PATH}${childSearch}`;
  }

  return null;
};

/**
 * 子应用 fullPath → 父地址栏业务 path
 *
 * 要求1：/platform/knowledge-bases?tenantId={spaceId} → /gamewiki/space/{spaceId}
 * 要求2：/platform/knowledge-bases/{kbId}?fromTenantId={spaceId} → /gamewiki/space/{spaceId}/kb/{kbId}
 * 个人空间：无 tenantId / fromTenantId 时 spaceId 为 personal
 */
export const gameWikiChildToParentPath: TChildToParentPath = (
  childFullPath,
  ctx,
) => {
  const mount = ctx.parentMountPath.replace(/\/$/, '');
  const { path, search } = splitPathAndSearch(childFullPath);
  const normalizedPath = normalizePath(path);

  if (normalizedPath === GAMEWIKI_KB_LIST_CHILD_PATH) {
    const tenantId = getQueryParam(search, 'tenantId');
    // 无 tenantId 表示个人空间，父 path 使用 personal
    const spaceId = tenantId ?? GAMEWIKI_PERSONAL_SPACE_ID;
    const parentSearch = buildSearch(search, {}, ['tenantId']);
    return `${mount}/space/${spaceId}${parentSearch}`;
  }

  if (normalizedPath.startsWith(GAMEWIKI_KB_DETAIL_CHILD_PREFIX)) {
    const kbId = normalizedPath.slice(GAMEWIKI_KB_DETAIL_CHILD_PREFIX.length);
    if (!kbId || kbId.includes('/')) return null;
    const fromTenantId = getQueryParam(search, 'fromTenantId');
    // 无 fromTenantId 表示个人空间，父 path 使用 personal
    const spaceId = fromTenantId ?? GAMEWIKI_PERSONAL_SPACE_ID;
    const parentSearch = buildSearch(search, {}, ['fromTenantId']);
    return `${mount}/space/${spaceId}/kb/${kbId}${parentSearch}`;
  }

  return null;
};
